import styled, { css } from 'styled-components'

// margin-top, não gap no pai: o único chamador (AudienceTargeting) sempre
// coloca a descrição da Conversão/Audiência VTEX logo acima -- sem isso, o
// texto renderiza colado no quadro de instrução (2026-09-22). Mesmo padrão já
// usado ali para BroadAudienceHint, que tinha o problema equivalente com o
// card de segmentação acima.
export const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sl-space-3);
  margin-top: var(--sl-space-3);
`

export const HiddenInput = styled.input`
  display: none;
`

// `--sl-border-*` já são o atalho `border` completo (largura + estilo + cor), não
// apenas uma cor -- o mesmo tropeço já documentado na correção da T010
// (AudienceTargeting/Channel). `border-color: var(--sl-border-accent-strong)`
// vira `border-color: 1px solid ...`, inválido, e o navegador descarta em
// silêncio. Por isso o token entra sempre por `border` (a declaração inteira),
// nunca por `border-color`/`border-style` isolados -- e a variante tracejada usa
// `border-style: dashed` **depois**, sobrescrevendo só o estilo herdado do atalho.
const draggingOver = css`
  border: var(--sl-border-accent-strong);
  border-style: dashed;
  background: var(--sl-bg-accent-hover);
`

// Zona inteira clicável e arrastável, não só o texto — o protótipo trata a área
// toda como alvo, não um botão pequeno dentro dela. Borda equivalente à do
// upload de mídias (`~/components/unform Input/FileInput`): tracejada, mesma
// identidade visual, tokens do Shoreline em vez do tema legado.
// all: unset também zera box-sizing para o inicial do CSS (content-box), mesmo
// havendo um reset global em border-box no resto do site -- all não herda de
// fora, reseta a propriedade em si. Sem a linha abaixo, width: 100% vira o
// conteúdo, e a borda + o padding horizontal se somam por cima, estourando a
// largura do card ao redor (a caixa selecionada nas devtools passando da borda
// do quadro pai).
// `$isDraggingOver`, não `isDraggingOver`: convenção já usada no resto do módulo
// (ReviewMediaCard, CardHeader, Budget) para prop transiente do styled-components --
// sem o `$`, `button` não declara essa prop no generic, o TS recusa o JSX que a passa,
// e o styled-components tentaria repassar `isdraggingover` para o DOM do <button>.
export const DropZone = styled.button<{ $isDraggingOver?: boolean }>`
  all: unset;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sl-space-1);
  width: 100%;
  padding: var(--sl-space-6) var(--sl-space-4);
  border: var(--sl-border-base);
  border-style: dashed;
  border-radius: var(--sl-radius-2);
  background: var(--sl-bg-base);
  cursor: pointer;
  text-align: center;

  &:hover,
  &:focus-visible {
    border: var(--sl-border-accent-strong-hover);
    border-style: dashed;
  }

  ${({ $isDraggingOver }) => $isDraggingOver && draggingOver}
`

// pointer-events: none nos três -- sem isso, mover o cursor de arrastar sobre o
// ícone ou o texto (a maior parte da área do alvo) dispara dragleave/dragenter
// no botão pai a cada fronteira cruzada, e o realce tracejado pisca durante o
// arraste. O clique continua funcionando: o evento passa direto para o botão.
export const DropZoneIcon = styled.span`
  color: var(--sl-fg-base-soft);
  font-size: 1.5rem;
  line-height: 1;
  pointer-events: none;
`

export const DropZoneAction = styled.span`
  color: var(--sl-fg-accent);
  font-weight: var(--sl-font-weight-medium);
  pointer-events: none;
`

export const DropZoneHint = styled.span`
  color: var(--sl-fg-base-soft);
  font: var(--sl-text-caption-1-font);
  pointer-events: none;
`

// Mesmo modelo de cartão do criativo já aceito
// (OffsiteCreativesList/styles.ts: Card > Head > Thumb/Meta), aplicado
// parcialmente: sem os campos por criativo (URL de destino, Meta), que não
// fazem sentido para o CSV. O ícone fixo ocupa o lugar da miniatura de imagem
// — não há preview possível para um CSV.
export const FileCard = styled.div`
  border: var(--sl-border-base);
  border-radius: var(--sl-radius-2);
  background: var(--sl-bg-base);
  padding: var(--sl-space-3);
  display: flex;
  align-items: center;
  gap: var(--sl-space-3);
`

export const FileIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: var(--sl-radius-2);
  background: var(--sl-bg-accent);
  color: var(--sl-fg-accent);
`

export const FileMeta = styled.span`
  min-width: 0;
  flex: 1;
`

export const FileName = styled.span`
  display: block;
  color: var(--sl-fg-base);
  font-weight: var(--sl-font-weight-medium);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export const FileSize = styled.span`
  display: block;
  color: var(--sl-fg-base-soft);
  font: var(--sl-text-caption-1-font);
`

export const ErrorText = styled.p`
  margin: 0;
  color: var(--sl-fg-critical);
  font: var(--sl-text-caption-1-font);
`
